import { inject, Injectable, signal } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en';
import { DateAdapter } from '@angular/material/core';
import { TranslocoService } from '@jsverse/transloco';
import { TranslocoLocaleService } from '@jsverse/transloco-locale';
import { catchError, firstValueFrom, forkJoin, of } from 'rxjs';
import moment from 'moment';
import {
  AVAILABLE_LANGS,
  DEFAULT_LANG,
  LANG_TO_LOCALE,
  LOCALE_STORAGE_KEY,
  resolveSupportedLang,
} from '../i18n/i18n.config';
import { I18N_SCOPES } from '../i18n/i18n-scopes';
import { User, UserService } from './user.service';
import { SsoAuthService } from '../auth/sso-auth.service';
import { ManagePreferencesService } from './manage-preferences.service';

// Angular ships en by default; registering keeps the pattern explicit so adding
// locales later is a one-line change (import + registerLocaleData).
registerLocaleData(localeEn, DEFAULT_LANG);

/**
 * Single source of truth for the active locale. It is the only place that mutates
 * the app language and keeps Transloco (text), TranslocoLocale (number/date/currency
 * formatting), the Material date adapter (moment) and the document lang in sync,
 * and persists the choice.
 *
 * Resolution order on startup: localStorage cache -> server preferences blob ->
 * Azure AD `preferredLanguage` -> default language.
 */
@Injectable({ providedIn: 'root' })
export class LocaleService {
  private readonly transloco = inject(TranslocoService);
  private readonly translocoLocale = inject(TranslocoLocaleService);
  private readonly dateAdapter = inject(DateAdapter<unknown>);
  private readonly userService = inject(UserService);
  private readonly ssoAuth = inject(SsoAuthService);
  private readonly preferences = inject(ManagePreferencesService);

  private readonly _lang = signal<string>(DEFAULT_LANG);
  /** Currently active application language (e.g. `en`). */
  public readonly lang = this._lang.asReadonly();

  private reconciled = false;

  /** Supported languages, exposed for the locale switcher UI. */
  public get availableLangs(): readonly string[] {
    return AVAILABLE_LANGS;
  }

  /**
   * Called from a (blocking) app initializer. Applies the cached (or default)
   * language, then preloads the global bundle + every lazy scope for that
   * language before the app renders. Preloading makes synchronous `translate()`
   * calls (field initializers, constructors, service defaults) resolve on first
   * paint instead of briefly rendering raw keys. After the user is available it
   * reconciles against server/SSO preferences.
   */
  public async init(): Promise<void> {
    this.applyLang(DEFAULT_LANG, false);
    await this.preloadActiveLang();
  }

  /** Eagerly loads the global bundle and all scopes for the active language. */
  private async preloadActiveLang(): Promise<void> {
    const lang = this._lang();
    const paths = [lang, ...I18N_SCOPES.map((scope) => `${scope}/${lang}`)];
    try {
      await firstValueFrom(
        forkJoin(paths.map((p) => this.transloco.load(p).pipe(catchError(() => of(null))))),
      );
    } catch {
      // Never block bootstrap on a translation fetch failure; pipes self-heal.
    }
  }

  /** User-driven language change (locale switcher / preferences). Persists everywhere. */
  public setLang(lang: string): void {
    const resolved = resolveSupportedLang(lang) ?? DEFAULT_LANG;
    this.applyLang(resolved, true);
    void this.preloadActiveLang();
    const user = this.userService.user();
    if (user?.id != null) {
      this.persistToServer(user, resolved);
    }
  }

  private applyLang(lang: string, persist: boolean): void {
    const resolved = AVAILABLE_LANGS.includes(lang) ? lang : DEFAULT_LANG;
    this._lang.set(resolved);
    this.transloco.setActiveLang(resolved);

    const intlLocale = LANG_TO_LOCALE[resolved] ?? LANG_TO_LOCALE[DEFAULT_LANG];
    this.translocoLocale.setLocale(intlLocale);
    moment.locale(intlLocale.toLowerCase());
    this.dateAdapter.setLocale(intlLocale);

    if (typeof document !== 'undefined') {
      document.documentElement.lang = resolved;
    }

    if (persist) {
      writeStoredLang(resolved);
    }
  }

  private reconcileFromUser(user: User | null): void {
    if (this.reconciled || !user?.id) {
      return;
    }
    // A locally stored choice always wins; nothing to reconcile.
    if (readStoredLang()) {
      this.reconciled = true;
      return;
    }
    this.preferences
      .getUserPreferences(user.id, user.activeTeamId ?? 0, user.defaultTenant ?? 0)
      .pipe(catchError(() => of(null)))
      .subscribe((res) => {
        const serverLang = resolveSupportedLang(parseLangFromBlob(res?.preferences));
        const ssoLang = resolveSupportedLang(this.ssoAuth.user$.value?.preferredLanguage);
        const resolved = serverLang ?? ssoLang;
        if (resolved && resolved !== this._lang()) {
          this.applyLang(resolved, true);
          void this.preloadActiveLang();
        }
        this.reconciled = true;
      });
  }

  private persistToServer(user: User, lang: string): void {
    // Read-modify-write so we don't clobber other keys (e.g. widget prefs) in the blob.
    this.preferences
      .getUserPreferences(user.id!, user.activeTeamId ?? 0, user.defaultTenant ?? 0)
      .pipe(catchError(() => of(null)))
      .subscribe((res) => {
        const blob = parseBlob(res?.preferences);
        blob['locale'] = lang;
        this.preferences
          .saveUserPreferences({
            userId: user.id,
            teamId: user.activeTeamId ?? user.defaultTeamId,
            tenant: user.defaultTenant,
            preferences: JSON.stringify(blob),
          })
          .pipe(catchError(() => of(null)))
          .subscribe();
      });
  }
}

function readStoredLang(): string | null {
  try {
    return localStorage.getItem(LOCALE_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredLang(lang: string): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, lang);
  } catch {
    // ignore quota / private mode
  }
}

function parseBlob(raw: string | null | undefined): Record<string, unknown> {
  if (!raw) {
    return {};
  }
  try {
    return JSON.parse(raw.replace(/'/g, '"')) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function parseLangFromBlob(raw: string | null | undefined): string | null {
  const blob = parseBlob(raw);
  const value = blob['locale'];
  return typeof value === 'string' ? value : null;
}
